import { useCallback, useEffect, useRef, type ReactNode } from "react"
import { useAuth } from "react-oidc-context"
import { AUTH_DISABLED } from "@/authConfig"
import { setAccessTokenGetter } from "@/lib/token-getter"
import { useSessionExpiration } from "@/hooks/use-session-expiration"
import { toast } from "sonner"
import { AuthLoadingScreen } from "./auth-loading-screen"
import { AuthErrorScreen } from "./auth-error-screen"
import { identifyUser, resetAnalytics } from "@/analytics"
import { appInsightsService } from "@/services/app-insights"

interface AppWithAuthProps {
  children: ReactNode
}

export function AppWithAuth({ children }: Readonly<AppWithAuthProps>) {
  const auth = useAuth()
  const hasAttemptedSignin = useRef(false)
  const sessionExpirationHandledRef = useRef(false)

  const handleSessionExpiration = useCallback(() => {
    if (AUTH_DISABLED || sessionExpirationHandledRef.current) {
      return
    }
    sessionExpirationHandledRef.current = true
    toast.error("Your session has expired. Please log in again.")
    auth.signinRedirect()
  }, [auth])

  useSessionExpiration(handleSessionExpiration)


  // Keep Application Insights user context in sync with auth state.
  // We prefer stable, non-PII identifiers from OIDC claims when available.
  useEffect(() => {

    // Reset session expiration flag when user successfully logs back in
    if (auth.isAuthenticated && !auth.isLoading) {
      sessionExpirationHandledRef.current = false
    }

    if (AUTH_DISABLED) {
      appInsightsService.clearAuthenticatedUserContext()
      resetAnalytics()
      return
    }

    if (!auth.isAuthenticated || !auth.user?.profile) {
      appInsightsService.clearAuthenticatedUserContext()
      resetAnalytics()
      return
    }

    const profile = auth.user.profile as Record<string, unknown>
    const userId =
      (profile.sub as string | undefined) ||
      (profile.oid as string | undefined) ||
      (profile.preferred_username as string | undefined) ||
      (profile.email as string | undefined)
    const accountId =
      (profile.tid as string | undefined) ||
      (profile.tenantId as string | undefined)

    if (userId) {
      appInsightsService.setAuthenticatedUserContext(userId, accountId)
      identifyUser(userId, accountId)
    } else {
      appInsightsService.clearAuthenticatedUserContext()
      resetAnalytics()
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.user, auth.user?.profile])

  // Inject token getter when auth is available
  useEffect(() => {
    if (AUTH_DISABLED) {
      return
    }
    setAccessTokenGetter(() => auth.user?.access_token)
  }, [auth.user?.access_token])

  // Handle signin redirect in effect to avoid ref access during render
  useEffect(() => {
    if (AUTH_DISABLED) {
      return
    }
    if (!auth.isLoading && !auth.error && !auth.isAuthenticated) {
      if (!hasAttemptedSignin.current) {
        hasAttemptedSignin.current = true
        auth.signinRedirect()
      }
    }
  }, [auth])

  // If auth is disabled, render children directly
  if (AUTH_DISABLED) {
    return <>{children}</>
  }

  // Show loading screen while auth is initializing
  if (auth.isLoading) {
    return <AuthLoadingScreen />
  }

  // Show error screen if auth failed
  if (auth.error) {
    return (
      <AuthErrorScreen
        error={auth.error}
        onRetry={() => {
          hasAttemptedSignin.current = false
          auth.signinRedirect()
        }}
      />
    )
  }

  // Show loading screen while redirecting to login
  if (!auth.isAuthenticated) {
    return <AuthLoadingScreen />
  }

  return <>{children}</>
}
